import React, { useState, useEffect } from 'react';
import {
  X, Save, Search, Users, RefreshCw, CheckCircle,
  AlertCircle, User, Music, Plus
} from 'lucide-react';

// ----------------------------------------------------------------
// ArtSchoolFamilyModal
//
// TAB 1 — School Family:
//   Search existing school families → see their children from
//   Students table → pick which child to register in art school.
//   If child already has ArtSchoolStudents record, skip creation.
//
// TAB 2 — External Family:
//   Toggle: Adult (registering themselves) vs Child
//   Adult: single Full Name field, student auto-created with same name
//   Child: Father/Mother fields + child name below
//   Both use 7369-XXXXX FamilyID format
// ----------------------------------------------------------------

const ArtSchoolFamilyModal = ({ onClose, onSave, apiCall, selectedYear }) => {
  const [mode, setMode] = useState('school'); // 'school' | 'external'

  // ── School mode state ──────────────────────────────────────
  const [searchQ, setSearchQ]               = useState('');
  const [searchResults, setSearchResults]   = useState([]);
  const [searching, setSearching]           = useState(false);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [schoolChildren, setSchoolChildren] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [selectedChild, setSelectedChild]   = useState(null);

  // ── External mode state ────────────────────────────────────
  const [isAdult, setIsAdult]               = useState(false);
  const [nextFamilyID, setNextFamilyID]     = useState('');
  const [loadingID, setLoadingID]           = useState(false);
  const [extForm, setExtForm] = useState({
    fullName:       '',
    fatherName:     '',
    motherName:     '',
    primaryPhone:   '',
    email:          '',
    address:        '',
    notes:          '',
    childFirstName: '',
    childLastName:  '',
    childDOB:       '',
    childGender:    ''
  });

  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  // ── Generate next 7369-XXXXX FamilyID ─────────────────────
  const generateNextID = async () => {
    setLoadingID(true);
    try {
      const families = await apiCall('/families');
      const prefix = '7369-';
      let maxNum = 0;
      families.forEach(f => {
        if (f.FamilyID && f.FamilyID.startsWith(prefix)) {
          const num = parseInt(f.FamilyID.replace(prefix, ''), 10);
          if (!isNaN(num) && num > maxNum) maxNum = num;
        }
      });
      setNextFamilyID(maxNum > 0
        ? `${prefix}${String(maxNum + 1).padStart(5, '0')}`
        : `${prefix}00001`);
    } catch {
      setNextFamilyID(`7369-${Date.now().toString().slice(-5)}`);
    } finally {
      setLoadingID(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (mode === 'external') generateNextID();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // ── Search school families ─────────────────────────────────
  useEffect(() => {
    if (searchQ.trim().length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        setSearchResults(await apiCall(
          `/artschool/families/search?q=${encodeURIComponent(searchQ)}&mode=register`
        ));
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQ]);

  // ── Load children when school family selected ──────────────
  useEffect(() => {
    if (!selectedFamily) { setSchoolChildren([]); setSelectedChild(null); return; }
    setLoadingChildren(true);
    apiCall(`/artschool/families/${selectedFamily.FamilyID}/school-children?academicYear=${selectedYear}`)
      .then(data => setSchoolChildren(data))
      .catch(() => setSchoolChildren([]))
      .finally(() => setLoadingChildren(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFamily]);

  const setExt = (field, val) => setExtForm(f => ({ ...f, [field]: val }));

  // ── Save ───────────────────────────────────────────────────
  const handleSave = async () => {
    setError('');

    if (mode === 'school') {
      if (!selectedFamily) { setError('Please select a school family.'); return; }
      if (!selectedChild)  { setError('Please select a child to register.'); return; }
      const child = schoolChildren.find(c => c.StudentID === selectedChild);
      setSaving(true);
      try {
        await onSave({ mode: 'school', familyID: selectedFamily.FamilyID, child });
        onClose();
      } catch (err) {
        setError(err.message || 'Failed to register. Please try again.');
      } finally { setSaving(false); }

    } else {
      if (isAdult) {
        if (!extForm.fullName.trim()) { setError('Please enter the full name.'); return; }
      } else {
        if (!extForm.fatherName.trim() && !extForm.motherName.trim())
          { setError('Please enter at least a father or mother name.'); return; }
        if (!extForm.childFirstName.trim() || !extForm.childLastName.trim())
          { setError("Please enter the child's name."); return; }
      }

      setSaving(true);
      try {
        await onSave({
          mode:            'external',
          isAdult,
          familyID:        nextFamilyID,
          fatherName:      isAdult ? extForm.fullName      : extForm.fatherName    || null,
          motherName:      isAdult ? null                  : extForm.motherName    || null,
          primaryPhone:    extForm.primaryPhone || null,
          email:           extForm.email        || null,
          address:         extForm.address      || null,
          notes:           extForm.notes        || null,
          isArtSchoolOnly: true,
          studentFirstName: isAdult ? extForm.fullName        : extForm.childFirstName,
          studentLastName:  isAdult ? ''                      : extForm.childLastName,
          studentDOB:       isAdult ? null                    : extForm.childDOB    || null,
          studentGender:    isAdult ? null                    : extForm.childGender || null,
        });
        onClose();
      } catch (err) {
        setError(err.message || 'Failed to register. Please try again.');
      } finally { setSaving(false); }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Users size={20} className="text-indigo-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-800">Register for Art School</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-5">

          {/* Mode Toggle */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => { setMode('school'); setSelectedFamily(null); setSearchQ(''); }}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors
                ${mode === 'school' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              School Family
            </button>
            <button
              onClick={() => setMode('external')}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors
                ${mode === 'external' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              External / New
            </button>
          </div>

          {/* ══ SCHOOL FAMILY MODE ═══════════════════════════ */}
          {mode === 'school' && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">
                Search for an existing school family, then select which child is enrolling in art school.
              </p>

              <div className="relative">
                <Search size={16} className="absolute left-3 top-3 text-gray-400" />
                <input
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Search by name or phone..."
                  value={searchQ}
                  onChange={e => {
                    setSearchQ(e.target.value);
                    setSelectedFamily(null);
                    setSchoolChildren([]);
                    setSelectedChild(null);
                  }}
                />
                {searching && <RefreshCw size={14} className="absolute right-3 top-3 text-gray-400 animate-spin" />}
              </div>

              {searchResults.length > 0 && !selectedFamily && (
                <div className="border border-gray-200 rounded-lg divide-y max-h-44 overflow-y-auto">
                  {searchResults.map(f => (
                    <button
                      key={f.FamilyID}
                      onClick={() => { setSelectedFamily(f); setSearchQ(f.FamilyDisplayName || f.FamilyID); }}
                      className="w-full text-left px-4 py-3 hover:bg-indigo-50 transition-colors"
                    >
                      <div className="font-medium text-gray-800">{f.FamilyDisplayName || f.FamilyID}</div>
                      <div className="text-xs text-gray-500 flex gap-3 mt-0.5">
                        <span>{f.FamilyID}</span>
                        {f.PrimaryPhone && <span>{f.PrimaryPhone}</span>}
                        {f.ArtStudentCount > 0 && (
                          <span className="text-indigo-600">{f.ArtStudentCount} already in art school</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {searchQ.length >= 2 && searchResults.length === 0 && !searching && (
                <p className="text-sm text-gray-400 text-center py-2">
                  No families found. Try the External tab to create a new one.
                </p>
              )}

              {selectedFamily && (
                <div className="space-y-3">
                  {/* Family badge */}
                  <div className="flex items-center justify-between p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <div>
                      <div className="font-medium text-indigo-800">
                        {selectedFamily.FamilyDisplayName || selectedFamily.FamilyID}
                      </div>
                      <div className="text-xs text-indigo-500">
                        {selectedFamily.FamilyID} · {selectedFamily.PrimaryPhone}
                      </div>
                    </div>
                    <button
                      onClick={() => { setSelectedFamily(null); setSchoolChildren([]); setSelectedChild(null); setSearchQ(''); }}
                      className="text-xs text-gray-400 hover:text-gray-600 underline"
                    >
                      Change
                    </button>
                  </div>

                  {/* Children list */}
                  <p className="text-sm font-medium text-gray-700">Select which child is enrolling:</p>
                  {loadingChildren ? (
                    <div className="flex items-center gap-2 text-sm text-gray-400 py-3">
                      <RefreshCw size={14} className="animate-spin" /> Loading children...
                    </div>
                  ) : schoolChildren.length === 0 ? (
                    <p className="text-sm text-gray-400 py-2">No active students found for this family.</p>
                  ) : (
                    <div className="space-y-2">
                      {schoolChildren.map(child => {
                        const isSelected = selectedChild === child.StudentID;
                        return (
                          <button
                            key={child.StudentID}
                            onClick={() => setSelectedChild(child.StudentID)}
                            className={`w-full text-left p-3 rounded-lg border transition-colors
                              ${isSelected
                                ? 'bg-indigo-600 border-indigo-600 text-white'
                                : 'bg-white border-gray-200 hover:border-indigo-300'}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className={`p-1.5 rounded-full ${isSelected ? 'bg-indigo-500' : 'bg-gray-100'}`}>
                                  <User size={14} className={isSelected ? 'text-white' : 'text-gray-500'} />
                                </div>
                                <div>
                                  <div className={`font-medium text-sm ${isSelected ? 'text-white' : 'text-gray-800'}`}>
                                    {child.FullName}
                                  </div>
                                  <div className={`text-xs ${isSelected ? 'text-indigo-200' : 'text-gray-400'}`}>
                                    {child.Grade
                                      ? `${child.Grade}${child.Section ? ' ' + child.Section : ''}`
                                      : 'No class assigned'}
                                  </div>
                                </div>
                              </div>
                              <div>
                                {child.AlreadyInArtSchool ? (
                                  <div className="flex items-center gap-1">
                                    <Music size={12} className={isSelected ? 'text-indigo-200' : 'text-indigo-500'} />
                                    <span className={`text-xs ${isSelected ? 'text-indigo-200' : 'text-indigo-600'}`}>
                                      {child.ActiveArtClasses > 0
                                        ? `${child.ActiveArtClasses} class${child.ActiveArtClasses > 1 ? 'es' : ''}`
                                        : 'In art school'}
                                    </span>
                                  </div>
                                ) : (
                                  <span className={`text-xs ${isSelected ? 'text-indigo-200' : 'text-gray-400'}`}>
                                    New to art school
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Info if already in art school */}
                  {selectedChild && (() => {
                    const child = schoolChildren.find(c => c.StudentID === selectedChild);
                    if (child?.AlreadyInArtSchool) {
                      return (
                        <div className="flex gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                          <CheckCircle size={16} className="shrink-0 mt-0.5 text-blue-500" />
                          <span>
                            {child.FullName} already has an art school record — saving will take you straight to enrollment.
                            {child.EnrolledClasses && ` Currently in: ${child.EnrolledClasses}.`}
                          </span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>
          )}

          {/* ══ EXTERNAL FAMILY MODE ═════════════════════════ */}
          {mode === 'external' && (
            <div className="space-y-4">

              {/* Adult / Child toggle */}
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setIsAdult(false)}
                  className={`flex-1 py-2 text-sm font-medium transition-colors
                    ${!isAdult ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  Registering a Child
                </button>
                <button
                  onClick={() => setIsAdult(true)}
                  className={`flex-1 py-2 text-sm font-medium transition-colors
                    ${isAdult ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  Adult Student
                </button>
              </div>

              {/* Family ID */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Family ID</label>
                <div className="flex gap-2">
                  <input
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 font-mono text-sm text-gray-600"
                    value={loadingID ? 'Generating...' : nextFamilyID}
                    onChange={e => setNextFamilyID(e.target.value)}
                  />
                  <button
                    onClick={generateNextID}
                    disabled={loadingID}
                    className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 text-gray-500"
                  >
                    <RefreshCw size={15} className={loadingID ? 'animate-spin' : ''} />
                  </button>
                </div>
              </div>

              {/* ADULT FLOW */}
              {isAdult && (
                <div className="space-y-3">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                    The adult is both the payer and the student. Their name will be used for billing and enrollment.
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                      value={extForm.fullName}
                      onChange={e => setExt('fullName', e.target.value)}
                      placeholder="First and Last Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                      value={extForm.primaryPhone}
                      onChange={e => setExt('primaryPhone', e.target.value)}
                      placeholder="+961 xx xxx xxx"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                      value={extForm.email}
                      onChange={e => setExt('email', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                      rows={2}
                      value={extForm.notes}
                      onChange={e => setExt('notes', e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* CHILD FLOW */}
              {!isAdult && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Father Name</label>
                      <input
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                        value={extForm.fatherName}
                        onChange={e => setExt('fatherName', e.target.value)}
                        placeholder="First Last"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Mother Name</label>
                      <input
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                        value={extForm.motherName}
                        onChange={e => setExt('motherName', e.target.value)}
                        placeholder="First Last"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                      value={extForm.primaryPhone}
                      onChange={e => setExt('primaryPhone', e.target.value)}
                      placeholder="+961 xx xxx xxx"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                      value={extForm.email}
                      onChange={e => setExt('email', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                      value={extForm.address}
                      onChange={e => setExt('address', e.target.value)}
                      placeholder="City, Area"
                    />
                  </div>

                  {/* Child info */}
                  <div className="border-t pt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                      <Plus size={14} /> Child being enrolled
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">First Name *</label>
                        <input
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                          value={extForm.childFirstName}
                          onChange={e => setExt('childFirstName', e.target.value)}
                          placeholder="First name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Last Name *</label>
                        <input
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                          value={extForm.childLastName}
                          onChange={e => setExt('childLastName', e.target.value)}
                          placeholder="Last name"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Date of Birth</label>
                        <input
                          type="date"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                          value={extForm.childDOB}
                          onChange={e => setExt('childDOB', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Gender</label>
                        <select
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                          value={extForm.childGender}
                          onChange={e => setExt('childGender', e.target.value)}
                        >
                          <option value="">—</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                      rows={2}
                      value={extForm.notes}
                      onChange={e => setExt('notes', e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle size={16} />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 border-t bg-gray-50 rounded-b-xl sticky bottom-0">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <Save size={16} />
            {saving ? 'Saving...' : 'Register'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArtSchoolFamilyModal;
